/**
 * One WebSocket per channel to its Durable Object.
 *
 *  - Opens AFTER first paint (the page is already showing the KV snapshot).
 *  - Sends {type:"hello", lastSeq} so the DO can replay only what we missed.
 *  - Reconnects with exponential backoff (1s, 2s, 4s … max 30s) and on tab focus/online.
 *  - Writes connection state into connectionStore; data goes to the domain store via callbacks.
 *    Nothing here touches React.
 */
import { connectionStore, type ConnectionState } from "../stores/connectionStore";
import type { ChannelId, ClientMessage, MeetingDelta, OrgDelta, ServerMessage, MeetingSnapshot, OrgSnapshot } from "./protocol";

export interface LiveChannelOptions {
  url: string;                                    // e.g. `wss://${location.host}/live/meeting/${id}`
  channel: ChannelId;
  getLastSeq: () => number;
  onSnapshot: (data: MeetingSnapshot | OrgSnapshot) => void;
  onDelta: (seq: number, delta: MeetingDelta | OrgDelta) => void;
  /** Leave connectionStore alone: a background channel on a page that shows another channel's status. */
  quiet?: boolean;
}

const MAX_BACKOFF = 30_000;
const PING_EVERY = 25_000;   // keeps intermediaries from idling the socket out
const OFFLINE_FALLBACK = 30_000; // after this long disconnected, show "Snapshot · time"

export class LiveChannel {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private closedByUs = false;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private downSince: number | null = null;

  constructor(private opts: LiveChannelOptions) {}

  private status(s: Partial<ConnectionState>) {
    if (!this.opts.quiet) connectionStore.setState(s);
  }

  start() {
    this.closedByUs = false;
    window.addEventListener("online", this.reconnectNow);
    document.addEventListener("visibilitychange", this.onVisibility);
    this.open();
  }

  stop() {
    this.closedByUs = true;
    window.removeEventListener("online", this.reconnectNow);
    document.removeEventListener("visibilitychange", this.onVisibility);
    clearInterval(this.pingTimer);
    clearTimeout(this.retryTimer);
    this.ws?.close(1000, "bye");
    this.ws = null;
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private open() {
    this.status({ status: this.attempts ? "reconnecting" : "connecting", attempts: this.attempts });
    const ws = new WebSocket(this.opts.url);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.attempts = 0;
      this.downSince = null;
      this.send({ type: "hello", channel: this.opts.channel, lastSeq: this.opts.getLastSeq() });
      this.status({ status: "live", attempts: 0 });
      clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => this.send({ type: "ping" }), PING_EVERY);
    });

    ws.addEventListener("message", (ev) => {
      let msg: ServerMessage;
      try { msg = JSON.parse(String(ev.data)); } catch { return; }
      if (msg.type === "snapshot") {
        this.opts.onSnapshot(msg.data);
        this.status({ snapshotAt: msg.data.at });
      } else if (msg.type === "delta") {
        this.opts.onDelta(msg.seq, msg.delta);
      } else if (msg.type === "error" && msg.code === "forbidden") {
        this.stop(); // role/org changed; RoleGate + router will handle the UI
      }
    });

    ws.addEventListener("close", () => {
      clearInterval(this.pingTimer);
      if (this.closedByUs) return;
      this.downSince ??= Date.now();
      const offlineTooLong = Date.now() - this.downSince > OFFLINE_FALLBACK;
      this.status({ status: offlineTooLong ? "snapshot" : "reconnecting" });
      this.scheduleRetry();
    });
  }

  private scheduleRetry() {
    clearTimeout(this.retryTimer);
    const delay = Math.min(MAX_BACKOFF, 1000 * 2 ** this.attempts) * (0.8 + Math.random() * 0.4); // jitter
    this.attempts++;
    this.retryTimer = setTimeout(() => this.open(), delay);
  }

  private reconnectNow = () => {
    if (this.closedByUs || this.ws?.readyState === WebSocket.OPEN) return;
    clearTimeout(this.retryTimer);
    this.open();
  };

  private onVisibility = () => {
    if (document.visibilityState === "visible") this.reconnectNow();
  };
}
